import React, {
  useEffect,
  useReducer,
  useState,
  useMemo,
  useRef
} from 'react';
import proptypes from 'prop-types';
import { Drawer } from '@one-thd/sui-atomic-components';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  useLazyDataModel,
  params,
  string as stringType,
  shape as shapeType,
  arrayOf,
  bool as boolType
} from '@thd-nucleus/data-sources';
import { ShareDetails } from './shareSteps/ShareDetails';
import { RecipientDetails } from './shareSteps/RecipientDetails';
import {
  generateSuccessMessage,
  generateFailureMessage,
  getSuccessRecipients,
  getFailedRecipients
} from './helpers';
import { triggerAnalytics, isValidUrl, isWorkspaceDashboard } from '../helpers';
import { SHARE_YOUR_PROJECT_SEND, PROJECTS_INTERACTION } from '../constants';

const { bool, func, shape, string } = proptypes;

export const initialState = {
  currentView: 'SHARE_DETAILS',
  steps: [
    { stepName: 'SHARE_DETAILS', isActive: true },
    { stepName: 'RECIPIENT_DETAILS', isActive: false }
  ]
};

export const assignProAllowanceStepReducer = (state, action) => {
  switch (action.type) {
  case 'GO_TO': {
    const targetStep = state.steps.find((step) => step.stepName === action.target);
    if (!targetStep) {
      return state;
    }

    return {
      currentView: action.target,
      steps: state.steps.map((step) => ({
        ...step,
        isActive: step.stepName === action.target
      }))
    };
  }
  default:
    return state;
  }
};

const validationSchema = yup.object().shape({
  whatShared: yup.string().required().oneOf(['entire-project', 'groups'], 'Select a share option.'),
  groupsShared: yup
    .array()
    .of(yup.string())
    .when('whatShared', {
      is: 'groups',
      then: (schema) => {
        return schema.of(yup.string()).min(1, 'Select groups to share.');
      },
      otherwise: (schema) => schema
    }),
  pricingChoice: yup.string().required().oneOf(['yes', 'no'], 'Select a pricing option.'),
  recipients: yup
    .array()
    .of(
      yup
        .string('Provide a valid email address. Example: you@domain.com')
        .trim()
        .required('Provide an email address.')
    )
    .transform((emailString) => {
      return Array.from(new Set(emailString.replaceAll(' ', '').split(',')));
    })
    .test(
      'email-validation',
      'Provide valid email addresses. Example: you@domain.com',
      (emails) => {
        const invalidEmails = emails.filter((email) => !yup.string().email().isValidSync(email));
        if (invalidEmails.length > 0) {
          throw new yup.ValidationError(
            emails.length > 1
              ? 'Provide valid email addresses. Example: you@domain.com'
              : 'Provide a valid email address. Example: you@domain.com',
            null,
            'recipients'
          );
        }
        return true;
      }
    )
    .max(10, 'Enter up to 10 email addresses.'),
  message: yup
    .string()
    .trim()
    .required('Tell your recipient about what you\'re sharing.')
    .max(1000, 'Message can\'t be longer than 1,000 characters.')
    .test('message',
      (message) => {
        if (isValidUrl(message)) {
          throw new yup.ValidationError('URLs are not allowed in messages.',
            null,
            'message');
        }
        return true;
      })
});

export const ShareProjectDrawer = ({
  open,
  setOpen,
  project,
  onSuccessMessage,
  onFailureMessage
}) => {
  const isMountedRef = useRef(true);
  const isInWorkspaceDashboard = isWorkspaceDashboard();
  const [drawerState, dispatch] = useReducer(assignProAllowanceStepReducer, initialState);
  const [isLoading, setIsLoading] = useState(false);
  const formMethods = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onTouched',
    defaultValues: {
      whatShared: '',
      groupsShared: [],
      pricingChoice: '',
      recipients: '',
      message: ''
    }
  });

  const [shareProject] = useLazyDataModel('createShareContext', {
    variables: {},
    context: { withAuth: true }
  });

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.trigger('ShareProjectDrawer.ready', {});
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const closeDrawer = () => {
    triggerAnalytics(PROJECTS_INTERACTION, { action: 'close-share-overlay' });
    setOpen(false);
  };

  const getGroupNames = (groupsShared) => {
    return (project.itemGroups || [])
      .filter((group) => {
        return groupsShared.includes(group.id);
      })
      .map((group) => (group.isDefault ? 'Unsorted Materials' : group.groupName));
  };

  const onValid = (values) => {
    setIsLoading(true);
    return shareProject({
      variables: {
        projectId: project.id,
        listId: project.defaultListId,
        listGroups: values.groupsShared,
        sharePrice: values.pricingChoice === 'yes',
        emails: values.recipients,
        message: values.message
      }
    })
      .then(({ data, errors }) => {
        if (errors || !data?.createShareContext) {
          onFailureMessage(
            generateFailureMessage({
              recipients: getFailedRecipients(errors)
            })
          );
        }
        if (data?.createShareContext) {
          const successRecipients = getSuccessRecipients(data.createShareContext);
          if (successRecipients.length > 0) {
            const analyticsEvent = isInWorkspaceDashboard
              ? PROJECTS_INTERACTION
              : SHARE_YOUR_PROJECT_SEND;
            const analyticsPayload = isInWorkspaceDashboard
              ? { action: 'share-action' }
              : { status: 'successful share' };
            triggerAnalytics(analyticsEvent, analyticsPayload);
            onSuccessMessage(
              generateSuccessMessage({
                recipients: getSuccessRecipients(data.createShareContext),
                whatShared: values?.whatShared,
                groupNamesShared: getGroupNames(values.groupsShared)
              })
            );
            setOpen(false);
          }
        }
      })
      .catch(() => {
        onFailureMessage('We\'re unable to share your project. Please try again.');
      })
      .finally(() => {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      });
  };

  const itemGroups = useMemo(() => {
    if (!project.itemGroups) {
      return [];
    }
    return project.itemGroups.filter((itemGroup) => {
      return !(itemGroup.groupName === '' && itemGroup.products.length === 0);
    });
  }, [project.itemGroups]);

  const itemCount = useMemo(() => {
    return (
      itemGroups?.reduce((acc, group) => {
        return (group.products.length || 0) + acc;
      }, 0) || 0
    );
  }, [itemGroups]);

  return (
    // The Drawer renders through a React portal to document.body. When this drawer
    // is opened from a themed experience (e.g. the B2B homepage Projects component),
    // the themed --sui-palette-* custom properties set on an ancestor cascade into
    // the portaled drawer and incorrectly theme it. Setting data-sui-palette="light"
    // on the drawer's root re-declares the default light palette on the drawer
    // element itself, overriding the inherited theme. See WOR2-864.
    <Drawer open={open} onClose={closeDrawer} DrawerRootProps={{ 'data-sui-palette': 'light' }}>
      <div data-component="ShareProjectDrawer">
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <FormProvider {...formMethods}>
          <form onSubmit={formMethods.handleSubmit(onValid)}>
            {drawerState.currentView === 'SHARE_DETAILS' && (
              <ShareDetails
                itemGroups={itemGroups}
                itemCount={itemCount}
                onClose={closeDrawer}
                onContinue={() => {
                  dispatch({ type: 'GO_TO', target: 'RECIPIENT_DETAILS' });
                }}
              />
            )}
            {drawerState.currentView === 'RECIPIENT_DETAILS' && (
              <RecipientDetails
                itemGroups={itemGroups}
                itemCount={itemCount}
                onClose={closeDrawer}
                onBack={() => {
                  dispatch({ type: 'GO_TO', target: 'SHARE_DETAILS' });
                }}
                isLoading={isLoading}
              />
            )}
          </form>
        </FormProvider>
      </div>
    </Drawer>
  );
};

ShareProjectDrawer.displayName = 'ShareProjectDrawer';

ShareProjectDrawer.propTypes = {
  open: bool.isRequired,
  setOpen: func.isRequired,
  project: shape({
    id: string,
    name: string
  }).isRequired,
  onSuccessMessage: func.isRequired,
  onFailureMessage: func.isRequired
};

ShareProjectDrawer.defaultProps = {};

ShareProjectDrawer.dataModel = {
  createShareContext: params({
    listId: stringType().isRequired(),
    listGroups: arrayOf(stringType().isRequired()).isRequired(),
    sharePrice: boolType().isRequired(),
    emails: arrayOf(stringType().isRequired()).isRequired(),
    message: stringType().isRequired(),
    projectId: stringType().isRequired()
  })
    .mutation()
    .shape({
      shareInitiatorName: stringType(),
      shareInitiatorEmail: stringType(),
      shareId: stringType().isRequired(),
      projectId: stringType().isRequired(),
      listId: stringType().isRequired(),
      listGroups: arrayOf(stringType().isRequired()).isRequired(),
      recipient: stringType().isRequired(),
      sharePrice: boolType().isRequired(),
      message: shapeType({
        from: stringType().isRequired(),
        content: stringType().isRequired()
      }).isRequired(),
      hasReply: boolType().isRequired()
    })
};
